(ns manage.content-variant-page.placeholder.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/CircularProgress$default" :as CircularProgress]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Typography$default" :as Typography]
            [common.modal.dialog :as dialog]
            [common.modal.view :refer [defmodal]]
            [common.ui.entity :refer [query-param->config]]
            [common.ui.list :as ui.list]
            [common.ui.store :as store]
            [common.ui.tabs :refer [tabs]]
            [common.widgets.layout :refer [stack-divider]]
            [common.widgets.toggle-button :refer [toggle-button-group]]
            [linked.core :as l]
            [manage.content-configuration-page.context-source.schema
             :refer [schema-id->icon]]
            [manage.content-configuration-page.context-source.view :as cs-view]
            [manage.content-configuration-page.view.list :as lv]
            [manage.content-variant-page.macro.view :as macro]
            [manage.content-variant-page.placeholder.db]
            [manage.content-variant-page.targeting-value.view :as targeting-value]
            [manage.content-variant-page.version-switcher.view :as version-switcher]
            [manage.content-variant-page.view.list
             :refer [configure-button list-item-details list-item-element]]
            [re-frame.core :as rf]
            [uix.core :refer [$ defui]]))

(defn targeting-values-string
  [tk]
  [list-item-element
   ($ Typography {:fontWeight 600} "Targeting values")
   ($ Typography {:noWrap true} @(rf/subscribe [:targeting-key/targeting-values-as-string tk]))])

(defn macro-list
  [p]
  [list-item-element
   ($ Typography {:fontWeight 600} "Triggers")
   (if-let [macros (not-empty @(rf/subscribe [:placeholder/unique-macros p]))]
     [:> Stack {:component "ul"
                :p 0}
      (doall (for [[macro-key [macro & _]] macros]
               ^{:key macro-key}
               [:> Stack {:component "li"
                          :direction "row"
                          :spacing 0.5
                          :alignItems "center"}
                [:> Icon {:font-size "small"} (schema-id->icon (get-in macro [:context-source/_macro :context-source/schema]))]
                [:span @(rf/subscribe [:macro/label macro " - "])]]))]
     ($ Typography {:fontStyle "italic"} "Must be configured"))])

(defn label-placeholder-name [p]
  @(store/get-value p :placeholder/name))

(defn placeholder-list-item-details
  [p]
  [list-item-details
   (when-let [tk @(rf/subscribe [:placeholder/targeting-key p])]
     [:div                              ; wrapped in div to avoid divider
      [macro-list p]
      [targeting-values-string tk]])
   [list-item-element
    [configure-button #(rf/dispatch [:placeholder/configure p])
     (if @(rf/subscribe [:resource.content-function-version/active-version? "published"])
       "View configuration"
       "Configure targeting")]]])

(defn placeholder-list-item-cfg
  [{p :eid :as placeholder}]
  {:entity/ref placeholder
   :entity/label [label-placeholder-name placeholder]
   :list-item/allow-delete-item? [:placeholder/allow-delete? p]
   :list-item/details [placeholder-list-item-details p]})

(defn list-cfg-placeholder
  [cc]
  {:entity/ref cc
   :list/items :content-configuration/placeholder
   :list/sort-by :placeholder/visualization-order
   :list/->item-cfg placeholder-list-item-cfg})

(defn targeting-key-list-item-cfg
  [tk]
  {:entity/ref tk
   :entity/label :variant-feed-key/name
   :entity/read-only? true
   :list-item/details
   [list-item-details
    [targeting-values-string (:eid tk)]
    [list-item-element
     [configure-button #(rf/dispatch [:targeting-key/configure (:eid tk)])
      (if @(rf/subscribe [:resource.content-function-version/active-version? "published"])
        "View configuration"
        "Configure targeting")]]]})

(defn list-cfg-targeting-keys-without-placeholder
  [vf]
  {:list/items [:variant-feed/targeting-keys-missing-placeholder (:eid vf)]
   :list/->item-cfg targeting-key-list-item-cfg})

(defn toggle-dynamic<->mapped [targeting-values-list p]
  (let [dynamic? @(rf/subscribe [:placeholder/dynamic? p])
        toggle-value (if dynamic? :dynamic :mapped)]
    (cond->> targeting-values-list
      @(rf/subscribe [:placeholder/allow-toggle-dynamic<->mapped? p])
      (conj [:> Stack {:divider ($ stack-divider)}
             [:> Box {:p 3}
              ($ toggle-button-group
                 {:options [{:value :dynamic
                             :disabled? (= toggle-value :dynamic)
                             :label "Dynamic targeting"
                             :on-click #(rf/dispatch [:modal/open {:id :placeholder/->dynamic-confirmation :props {:placeholder p}}])}
                            {:value :mapped
                             :disabled? (= toggle-value :mapped)
                             :label "Mapped targeting"
                             :on-click #(rf/dispatch [:placeholder/->mapped p])}]
                  :value toggle-value
                  :full-width true
                  :size "small"})]]))))

(defmodal ->dynamic-confirmation :placeholder/->dynamic-confirmation
  [{:keys [placeholder]}]
  [dialog/form-dialog
   {:modifier :warning
    :title (str "Are you sure you want to convert "
                @(rf/subscribe [:ui.store/value placeholder :placeholder/name])
                " to dynamic targeting?")
    :on-submit #(rf/dispatch [:placeholder/->dynamic placeholder])
    :cancel-button-label "Keep mapped targeting"
    :submit-button-label "Yes, convert to dynamic targeting"
    :submit-button-color "warning"}
   [:div
    [:p "By changing to dynamic targeting you will lose all existing manual mappings."]]])

(defn triggers+targeting-values [{p :eid :as placeholder}]
  (let [dynamic? @(rf/subscribe [:placeholder/dynamic? p])
        tk (store/->DataScriptDBEntity @(rf/subscribe [:placeholder/targeting-key p]))]
    ($ tabs {:tabs (l/map :triggers (lv/as-tab (macro/list-cfg p))
                          :targeting-values
                          (if dynamic?
                            (update (lv/as-tab (targeting-value/list-cfg-dynamic tk))
                                    :content (fn [targeting-values-list] [toggle-dynamic<->mapped targeting-values-list p]))
                            {:label "Mapped targeting values"
                             :content [toggle-dynamic<->mapped (targeting-value/list-mapped placeholder tk) p]}))
             :bind-active-tab-to-query-param true})))

(defn entity-view-cfg [placeholder]
  {:entity/ref placeholder
   :entity/icon "my_location"
   :entity/label :placeholder/name
   :entity/action-bar [version-switcher/action-bar]
   :entity/read-only? true
   :entity/content [triggers+targeting-values placeholder]})

(defui no-targeting-keys-message []
  ($ Box
     {:my 1
      :p 3
      :max-width "70rem"
      :data-testid :empty-msg}
     ($ ui.list/empty-msg {:title "Classify your targeting columns"}
        ($ :p
           "To set up targeting, classify column(s) of your feed as Targeting data, in your "
           ($ ui.list/empty-msg-button {:onClick #(rf/dispatch [:form.content-variant/open])}
              "feed configuration")
           ". These columns should contain the values that you want to use to target different audiences.")
        ($ :p
           "Once the targeting columns have been classified, you can configure
            them here by adding contextual triggers."))))

(defn- targeting-tab-panel [cc vf]
  (let [targeting-keys @(rf/subscribe [:variant-feed/targeting-keys (:eid vf)])]
    (if (empty? targeting-keys)
      ($ no-targeting-keys-message)
      [ui.list/concatenate
       lv/view
       (list-cfg-placeholder cc)
       (list-cfg-targeting-keys-without-placeholder vf)])))

(defn- targeting+dynamic-panel [cc vf]
  (let [cf-state @(rf/subscribe [:resource.content-function/state])]
    ;; Display a "circular progress" while the content function is loading.
    (cond
      (= cf-state :pending)
      [:> Stack {:sx {:height "100%"}
                 :justify-content "center"
                 :align-items "center"
                 :flex 1}
       [:> CircularProgress]]

      (= cf-state :success)
      ($ tabs {:tabs (l/map :targeting {:label "Targeting"
                                        :content [targeting-tab-panel cc vf]}
                            :dynamic (-> (cs-view/list-cfg cc)
                                         (lv/as-tab)
                                         (dissoc :icon)))
               :bind-active-tab-to-query-param true})

      :else
      nil)))

(defn view-cfg [cc vf]
  {:entity/ref cc
   :entity/icon "my_location"
   :entity/label "Targeting"
   :entity/action-bar [version-switcher/action-bar]
   :entity/read-only? true
   :entity/content [targeting+dynamic-panel cc vf]})

(defmethod query-param->config :placeholder
  [{placeholder-name :name}]
  (some-> @(rf/subscribe [:ui.store/lookup :placeholder/name placeholder-name])
          first store/->DataScriptDBEntity entity-view-cfg))
